import ErrorMessage from '../error-message/error-message';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../contexts';

export interface ResetPasswordFormProps {
  setShowForgotPassword: (showForgotPassword: boolean) => void;
  email?: string;
}

export function ResetPasswordForm({
  setShowForgotPassword,
  email,
}: ResetPasswordFormProps) {
  const [resetPasswordEmailSent, setResetPasswordEmailSent] = useState(false);
  const { t } = useTranslation(['common']);
  const { resetPassword } = useAuthUser();
  const { handleSubmit, values, errors, touched, handleChange } = useFormik({
    initialValues: {
      email: email || '',
    },
    onSubmit: (values, { resetForm, setErrors }) => {
      resetPassword(values.email)
        .then(() => {
          resetForm();
          setResetPasswordEmailSent(true);
        })
        .catch((error) => {
          const errorMessage = error.message;
          setErrors({ email: errorMessage });
        });
    },
    validationSchema: Yup.object({
      email: Yup.string().required('Required'),
    }),
  });

  if (resetPasswordEmailSent) {
    return (
      <div className="flex flex-col items-center justify-center">
        <h1 className="text-3xl font-bold leading-9 text-gray-900">
          {t('Reset Password Email Sent!')}
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          {t('Please check your email for a link to reset your password.')}
        </p>
        <button
          onClick={() => {
            setShowForgotPassword(false);
            setResetPasswordEmailSent(false);
          }}
          className="mt-10 bg-light-primary dark:bg-dark-primary hover:bg-dark-secondaryAccent text-white dark:text-custom-gray600 font-bold py-2 px-4 rounded"
        >
          {t('Back to Sign In')}
        </button>
      </div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div>
        <div className="flex items-center justify-between">
          <label
            htmlFor="email"
            className="block text-sm font-medium leading-6 text-gray-900 mr-10"
          >
            {t('Email address')}
          </label>
          <div className="text-sm">
            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              {t('Back to Sign In')}
            </button>
          </div>
        </div>
        <div className="mt-2">
          <input
            id="email"
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            required
            className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
          <ErrorMessage errors={errors.email} touched={touched.email} />
        </div>
      </div>

      <div>
        <button
          type="submit"
          className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          {t('Reset Password')}
        </button>
      </div>
    </form>
  );
}

export default ResetPasswordForm;
